import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

const MicContext = createContext(null);
const STORAGE_KEY = "smartdream_mic";

/**
 * Legge sincronicamente il permission salvato. Usato per inizializzare
 * useState così evitiamo flicker tra "unknown" → "granted" che farebbe
 * lampeggiare i banner "Microfono non attivo" in altre pagine.
 */
function readInitialPermission() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return "unknown";
    const parsed = JSON.parse(saved);
    return parsed?.permission || "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * MicContext — stato permesso microfono (granted/denied/skipped/unknown).
 *
 * Sorgente di verità: `users.mic_permission` su MongoDB (quando l'utente è
 * loggato). Localmente teniamo una cache in localStorage per startup veloce
 * e per gli utenti non ancora loggati.
 *
 * Quando l'utente cambia stato (accetta/rifiuta/salta il permesso) facciamo
 * PATCH /api/auth/me/preferences per propagare al backend → visibile in admin.
 */
export function MicProvider({ children }) {
  const { user, api } = useAuth();
  // Init lazy: il primo render ha già lo stato persistito → no flicker.
  const [permission, setPermission] = useState(readInitialPermission);
  const [recording, setRecording] = useState(false);

  // Browser Permissions API (best-effort) — solo se non abbiamo ancora deciso.
  useEffect(() => {
    if (permission !== "unknown") return;
    if (!navigator.permissions || !navigator.permissions.query) return;
    navigator.permissions
      .query({ name: "microphone" })
      .then((status) => {
        if (status.state && status.state !== "prompt") {
          setPermission((cur) => (cur === "unknown" ? status.state : cur));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (state) => {
    setPermission(state);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ permission: state }));
    } catch {
      /* ignore */
    }
    // Sync server (best-effort, solo se loggato)
    if (user?.id) {
      api
        .patch("/api/auth/me/preferences", { mic_permission: state })
        .catch(() => {
          // ritento al prossimo cambio; non c'è UX da bloccare per questo
        });
    }
  };

  const request = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      persist("granted");
      return "granted";
    } catch {
      persist("denied");
      return "denied";
    }
  };

  const skip = () => persist("skipped");

  const reset = () => {
    setPermission("unknown");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (user?.id) {
      api
        .patch("/api/auth/me/preferences", { mic_permission: "unknown" })
        .catch(() => {});
    }
  };

  const startMonitoring = () => setRecording(true);
  const stopMonitoring = () => setRecording(false);

  return (
    <MicContext.Provider
      value={{
        permission,
        recording,
        request,
        skip,
        reset,
        startMonitoring,
        stopMonitoring,
      }}
    >
      {children}
    </MicContext.Provider>
  );
}

export function useMic() {
  const ctx = useContext(MicContext);
  if (!ctx) throw new Error("useMic must be used within MicProvider");
  return ctx;
}
